/**
 * Authentication API client.
 * Handles login, registration, and user management endpoints.
 */

import apiClient from './client'
import { User, LoginRequest, RegisterRequest, Token } from '@types/index'

export const authAPI = {
  /**
   * Login with email and password.
   * Returns JWT token and user information.
   */
  async login(credentials: LoginRequest): Promise<Token> {
    return apiClient.post('/api/auth/login', credentials)
  },

  /**
   * Register a new user account.
   * Returns the created user object.
   */
  async register(data: RegisterRequest): Promise<User> {
    return apiClient.post('/api/auth/register', data)
  },

  /**
   * Get current authenticated user information.
   * SECURITY: Uses HttpOnly cookie for authentication (set by login endpoint).
   */
  async getCurrentUser(): Promise<User> {
    return apiClient.get('/api/auth/me')
  },

  /**
   * Refresh access token using refresh token.
   * SECURITY: Uses HttpOnly cookie for refresh token.
   */
  async refreshToken(): Promise<Token> {
    return apiClient.post('/api/auth/refresh')
  },

  /**
   * Logout user by clearing authentication cookie.
   * SECURITY: Clears HttpOnly cookie on backend.
   */
  async logout(): Promise<{ message: string }> {
    return apiClient.post('/api/auth/logout')
  },

  /**
   * Update current user profile.
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    return apiClient.put('/api/users/me', data)
  },

  /**
   * Update current user profile details (bio, avatar, etc.).
   */
  async updateProfileDetails(data: {
    bio?: string
    avatar_url?: string
    organization?: string
    location?: string
  }): Promise<User> {
    return apiClient.put('/api/users/me/profile', data)
  },

  /**
   * Delete current user account.
   */
  async deleteAccount(): Promise<void> {
    return apiClient.delete('/api/users/me')
  },

  /**
   * Request password reset by sending OTP to email.
   */
  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post('/api/auth/password-reset/request', { email })
  },

  /**
   * Reset password after OTP verification.
   */
  async resetPassword(
    email: string,
    otpCode: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.post('/api/auth/password-reset/reset', {
      email,
      otp_code: otpCode,
      new_password: newPassword,
    })
  },
}

